import { useEffect } from "react";

type SeoOptions = {
  title: string;
  description: string;
  keywords?: string;
  path?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

function upsertMeta(selector: string, create: () => HTMLMetaElement, set: (el: HTMLMetaElement) => void) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  set(el);
}

export function useSeo({ title, description, keywords, path = "/", jsonLd }: SeoOptions) {
  useEffect(() => {
    document.title = title;

    upsertMeta(
      'meta[name="description"]',
      () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "description");
        return m;
      },
      (el) => el.setAttribute("content", description),
    );

    if (keywords) {
      upsertMeta(
        'meta[name="keywords"]',
        () => {
          const m = document.createElement("meta");
          m.setAttribute("name", "keywords");
          return m;
        },
        (el) => el.setAttribute("content", keywords),
      );
    }

    upsertMeta(
      'meta[name="robots"]',
      () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "robots");
        return m;
      },
      (el) => el.setAttribute("content", "index, follow"),
    );

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const canonicalUrl = `${origin}${path}`;

    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonicalUrl);

    const ogMap: Record<string, string> = {
      "og:title": title,
      "og:description": description,
      "og:type": "website",
      "og:url": canonicalUrl,
      "og:site_name": "爆料大赛",
      "twitter:card": "summary_large_image",
      "twitter:title": title,
      "twitter:description": description,
    };
    Object.entries(ogMap).forEach(([key, value]) => {
      const attr = key.startsWith("og:") ? "property" : "name";
      upsertMeta(
        `meta[${attr}="${key}"]`,
        () => {
          const m = document.createElement("meta");
          m.setAttribute(attr, key);
          return m;
        },
        (el) => el.setAttribute("content", value),
      );
    });

    const scriptId = "seo-jsonld";
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (jsonLd) {
      if (!script) {
        script = document.createElement("script");
        script.id = scriptId;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
  }, [title, description, keywords, path, jsonLd]);
}